import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { verifyNIN, getStoredUser } from "../services/Request";
import { toast } from "react-toastify";
import {
  FaIdCard,
  FaCheckCircle,
  FaDatabase,
  FaGlobe,
  FaPrint,
  FaRedo,
  FaCopy,
  FaShieldAlt,
  FaUserCheck,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaCalendarAlt,
  FaVenusMars,
} from "react-icons/fa";

const formatPhoto = (photo) => {
  if (!photo) return null;
  const str = String(photo).trim();
  if (
    str.startsWith("data:") ||
    str.startsWith("http://") ||
    str.startsWith("https://")
  ) {
    return str;
  }
  return `data:image/jpeg;base64,${str}`;
};

export default function NINVerification() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);

  // Form State
  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  const [nin, setNin] = useState("");
  const [loading, setLoading] = useState(false);

  // Result State
  const [verificationResult, setVerificationResult] = useState(null);

  useEffect(() => {
    const user = getStoredUser();
    if (!user) {
      navigate("/login");
    } else {
      setCurrentUser(user);
    }
  }, [navigate]);

  const handleVerify = async (e) => {
    e.preventDefault();

    if (!nin || !firstname || !lastname) {
      toast.warning("Please fill in First Name, Last Name, and NIN.");
      return;
    }

    if (nin.trim().length < 5) {
      toast.warning(
        "NIN should be a valid identification number (usually 11 digits).",
      );
      return;
    }

    try {
      setLoading(true);
      const res = await verifyNIN({
        nin: nin.trim(),
        firstname: firstname.trim(),
        lastname: lastname.trim(),
        email: currentUser?.email,
      });

      if (res.status && res.data) {
        setVerificationResult(res);
        if (res.source === "database") {
          toast.info("Matching record retrieved from local database cache!");
        } else {
          toast.success("NIN verified successfully via QoreID registry!");
        }
      } else {
        toast.error(res.message || "NIN verification failed.");
      }
    } catch (err) {
      toast.error(err.message || "Failed to verify NIN.");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("NIN copied to clipboard!");
  };

  const handleReset = () => {
    setVerificationResult(null);
    setNin("");
    setFirstname("");
    setLastname("");
  };

  const resultData = verificationResult?.data;

  return (
    <div style={styles.page}>
      <Navbar />

      <main style={styles.mainContainer}>
        {/* Verification Form Section */}
        {!verificationResult ? (
          <div style={styles.formCard} className="fade-in">
            <div style={styles.formHeader}>
              <div style={styles.headerIcon}>
                <FaIdCard style={{ fontSize: "24px", color: "#064E3B" }} />
              </div>
              <div>
                <h1 style={styles.formTitle}>National Identity Verification</h1>
                <p style={styles.formSubtitle}>
                  Query the National Identity Registry or internal verified
                  database
                </p>
              </div>
            </div>

            <form onSubmit={handleVerify} style={styles.form}>
              <div style={styles.grid2}>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>
                    First Name <span style={{ color: "#DC2626" }}>*</span>
                  </label>
                  <input
                    type="text"
                    required
                    style={styles.input}
                    placeholder="e.g. Saqib or Emmanuel"
                    value={firstname}
                    onChange={(e) => setFirstname(e.target.value)}
                    disabled={loading}
                  />
                </div>

                <div style={styles.fieldGroup}>
                  <label style={styles.label}>
                    Last Name (Surname){" "}
                    <span style={{ color: "#DC2626" }}>*</span>
                  </label>
                  <input
                    type="text"
                    required
                    style={styles.input}
                    placeholder="e.g. Shahid or Okonkwo"
                    value={lastname}
                    onChange={(e) => setLastname(e.target.value)}
                    disabled={loading}
                  />
                </div>
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.label}>
                  National Identity Number (NIN){" "}
                  <span style={{ color: "#DC2626" }}>*</span>
                </label>
                <div style={styles.ninInputWrapper}>
                  <FaIdCard style={styles.ninIcon} />
                  <input
                    type="text"
                    required
                    maxLength={15}
                    style={styles.ninInput}
                    placeholder="Enter 11-digit NIN (e.g. 12345678901)"
                    value={nin}
                    onChange={(e) => setNin(e.target.value.replace(/\D/g, ""))}
                    disabled={loading}
                  />
                </div>
                <span style={styles.hint}>
                  Enter the 11-digit identification number printed on the
                  citizen's National e-ID card or slip.
                </span>
              </div>

              <div style={styles.actionRow}>
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    ...styles.submitBtn,
                    opacity: loading ? 0.75 : 1,
                    cursor: loading ? "not-allowed" : "pointer",
                  }}
                >
                  {loading ? (
                    <span>Verifying with Registry...</span>
                  ) : (
                    <>
                      <FaShieldAlt style={{ marginRight: "8px" }} />
                      Verify NIN Record
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        ) : (
          /* Verification Result Display */
          <div style={styles.resultContainer} className="fade-in">
            {/* Top Verification Alert Banner */}
            <div
              style={{
                ...styles.sourceAlert,
                backgroundColor:
                  verificationResult.source === "database"
                    ? "#EFF6FF"
                    : "#ECFDF5",
                borderColor:
                  verificationResult.source === "database"
                    ? "#BFDBFE"
                    : "#A7F3D0",
              }}
              className="no-print"
            >
              <div style={styles.sourceAlertLeft}>
                <div
                  style={{
                    ...styles.sourceIconBadge,
                    backgroundColor:
                      verificationResult.source === "database"
                        ? "#2563EB"
                        : "#059669",
                  }}
                >
                  {verificationResult.source === "database" ? (
                    <FaDatabase style={{ color: "#FFF" }} />
                  ) : (
                    <FaGlobe style={{ color: "#FFF" }} />
                  )}
                </div>
                <div>
                  <div style={styles.sourceAlertTitle}>
                    {verificationResult.source === "database"
                      ? "VERIFIED FROM INTERNAL DATABASE"
                      : "VERIFIED VIA QOREID REGISTRY"}
                  </div>
                  <div style={styles.sourceAlertDesc}>
                    {verificationResult.message}
                  </div>
                </div>
              </div>

              <div style={styles.sourceTag}>
                <FaCheckCircle style={{ marginRight: "6px" }} />
                STATUS: MATCH CONFIRMED
              </div>
            </div>

            {/* Official Digital Identity Card */}
            <div style={styles.idCard} id="printable-id-card">
              {/* Card Header */}
              <div style={styles.idHeader}>
                <div style={styles.coatOfArms}>
                  <FaShieldAlt style={{ fontSize: "28px", color: "#10B981" }} />
                </div>
                <div style={styles.idHeaderTexts}>
                  <div style={styles.idCountry}>
                    FEDERAL REPUBLIC OF NIGERIA
                  </div>
                  <div style={styles.idSubtitle}>
                    NATIONAL IDENTITY MANAGEMENT COMMISSION • DIGITAL IDENTITY
                    CARD
                  </div>
                </div>
                <div style={styles.statusBadge}>
                  <FaUserCheck style={{ marginRight: "5px" }} />
                  VERIFIED
                </div>
              </div>

              {/* Card Body */}
              <div style={styles.idBody}>
                {/* Photo Column */}
                <div style={styles.photoCol}>
                  <div style={styles.photoWrapper}>
                    {resultData.photo ? (
                      <img
                        src={formatPhoto(resultData.photo)}
                        alt="Citizen Passport"
                        style={styles.photoImg}
                      />
                    ) : (
                      <div style={styles.photoPlaceholder}>
                        <FaIdCard
                          style={{ fontSize: "48px", color: "#CBD5E1" }}
                        />
                      </div>
                    )}
                  </div>
                  <div style={styles.citizenCategory}>CITIZEN</div>
                </div>

                {/* Details Column */}
                <div style={styles.detailsCol}>
                  {/* NIN Highlight Row */}
                  <div style={styles.ninRow}>
                    <div style={styles.ninLabel}>
                      NATIONAL IDENTITY NUMBER (NIN):
                    </div>
                    <div style={styles.ninDisplay}>
                      <span style={styles.ninDigits}>{resultData.nin}</span>
                      <button
                        onClick={() => copyToClipboard(resultData.nin)}
                        style={styles.copyBtn}
                        className="no-print"
                        title="Copy NIN"
                      >
                        <FaCopy />
                      </button>
                    </div>
                  </div>

                  <div style={styles.infoGrid}>
                    <div style={styles.infoItem}>
                      <span style={styles.infoLabel}>SURNAME (LAST NAME)</span>
                      <span style={styles.infoValue}>
                        {resultData.surname?.toUpperCase() || "N/A"}
                      </span>
                    </div>

                    <div style={styles.infoItem}>
                      <span style={styles.infoLabel}>FIRST NAME</span>
                      <span style={styles.infoValue}>
                        {resultData.firstname?.toUpperCase() || "N/A"}
                      </span>
                    </div>

                    <div style={styles.infoItem}>
                      <span style={styles.infoLabel}>MIDDLE NAME</span>
                      <span style={styles.infoValue}>
                        {resultData.middlename?.toUpperCase() || "—"}
                      </span>
                    </div>

                    <div style={styles.infoItem}>
                      <span style={styles.infoLabel}>
                        <FaVenusMars style={{ marginRight: "4px" }} />
                        GENDER
                      </span>
                      <span style={styles.infoValue}>
                        {resultData.gender || "Not Specified"}
                      </span>
                    </div>

                    <div style={styles.infoItem}>
                      <span style={styles.infoLabel}>
                        <FaCalendarAlt style={{ marginRight: "4px" }} />
                        DATE OF BIRTH
                      </span>
                      <span style={styles.infoValue}>
                        {resultData.birthdate || "N/A"}
                      </span>
                    </div>

                    <div style={styles.infoItem}>
                      <span style={styles.infoLabel}>
                        <FaPhoneAlt style={{ marginRight: "4px" }} />
                        TELEPHONE
                      </span>
                      <span style={styles.infoValue}>
                        {resultData.phone || "N/A"}
                      </span>
                    </div>
                  </div>

                  {/* Address */}
                  <div style={styles.addressBox}>
                    <span style={styles.infoLabel}>
                      <FaMapMarkerAlt style={{ marginRight: "4px" }} />
                      REGISTERED RESIDENCE ADDRESS
                    </span>
                    <span style={styles.addressValue}>
                      {resultData.address ||
                        "Recorded in Federal NIMC Database"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Card Footer */}
              <div style={styles.idFooter}>
                <div style={styles.footerVerificationMeta}>
                  <span>
                    Verification Timestamp:{" "}
                    <strong>
                      {resultData.verifiedAt
                        ? new Date(resultData.verifiedAt).toLocaleString()
                        : new Date().toLocaleString()}
                    </strong>
                  </span>
                  <span>
                    {" "}
                    • Source Provider:{" "}
                    <strong>
                      {resultData.source?.toUpperCase() || "REGISTRY"}
                    </strong>
                  </span>
                </div>
                <div style={styles.securitySeal}>AUTHENTICATED RECORD</div>
              </div>
            </div>

            {/* Bottom Actions */}
            <div style={styles.bottomActions} className="no-print">
              <button
                type="button"
                onClick={() => window.print()}
                style={styles.printBtn}
              >
                <FaPrint style={{ marginRight: "8px" }} />
                Print Verification Slip
              </button>

              <button
                type="button"
                onClick={handleReset}
                style={styles.verifyAnotherBtn}
              >
                <FaRedo style={{ marginRight: "8px" }} />
                Verify Another NIN
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    backgroundColor: "#F8FAFC",
  },
  mainContainer: {
    maxWidth: "960px",
    margin: "40px auto",
    padding: "0 20px",
  },
  formCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: "20px",
    padding: "40px",
    boxShadow:
      "0 10px 25px -5px rgba(0, 0, 0, 0.05), 0 8px 10px -6px rgba(0, 0, 0, 0.02)",
    border: "1px solid #E2E8F0",
  },
  formHeader: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    marginBottom: "32px",
    paddingBottom: "20px",
    borderBottom: "1px solid #F1F5F9",
  },
  headerIcon: {
    width: "56px",
    height: "56px",
    borderRadius: "14px",
    backgroundColor: "#ECFDF5",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  formTitle: {
    fontSize: "22px",
    fontWeight: "800",
    color: "#0F172A",
    margin: "0 0 4px 0",
    letterSpacing: "-0.5px",
  },
  formSubtitle: {
    fontSize: "14px",
    color: "#64748B",
    margin: 0,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
  },
  grid2: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "20px",
  },
  fieldGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  label: {
    fontSize: "13px",
    fontWeight: "700",
    color: "#334155",
  },
  input: {
    padding: "13px 16px",
    fontSize: "14px",
    border: "1px solid #CBD5E1",
    borderRadius: "10px",
    backgroundColor: "#FFFFFF",
    color: "#1E293B",
    outline: "none",
    transition: "border-color 0.2s",
  },
  ninInputWrapper: {
    position: "relative",
    display: "flex",
    alignItems: "center",
  },
  ninIcon: {
    position: "absolute",
    left: "16px",
    fontSize: "18px",
    color: "#064E3B",
  },
  ninInput: {
    width: "100%",
    padding: "15px 16px 15px 48px",
    fontSize: "16px",
    fontWeight: "700",
    letterSpacing: "1px",
    border: "2px solid #CBD5E1",
    borderRadius: "12px",
    backgroundColor: "#F8FAFC",
    color: "#064E3B",
    outline: "none",
  },
  hint: {
    fontSize: "12px",
    color: "#64748B",
  },
  actionRow: {
    marginTop: "8px",
  },
  submitBtn: {
    width: "100%",
    padding: "16px",
    fontSize: "16px",
    fontWeight: "700",
    color: "#FFFFFF",
    backgroundColor: "#064E3B",
    border: "none",
    borderRadius: "12px",
    boxShadow: "0 4px 6px -1px rgba(6, 78, 59, 0.25)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background-color 0.2s",
  },
  sampleSection: {
    marginTop: "32px",
    paddingTop: "24px",
    borderTop: "1px dashed #E2E8F0",
  },
  sampleLabel: {
    fontSize: "12px",
    fontWeight: "700",
    color: "#64748B",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    display: "block",
    marginBottom: "12px",
  },
  sampleButtons: {
    display: "flex",
    gap: "12px",
    flexWrap: "wrap",
  },
  sampleBtn: {
    padding: "8px 16px",
    fontSize: "12px",
    fontWeight: "600",
    color: "#064E3B",
    backgroundColor: "#ECFDF5",
    border: "1px solid #A7F3D0",
    borderRadius: "8px",
    cursor: "pointer",
  },
  resultContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
  },
  sourceAlert: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "18px 24px",
    borderRadius: "16px",
    border: "1px solid",
  },
  sourceAlertLeft: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
  },
  sourceIconBadge: {
    width: "44px",
    height: "44px",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "18px",
  },
  sourceAlertTitle: {
    fontSize: "14px",
    fontWeight: "800",
    letterSpacing: "0.5px",
    color: "#0F172A",
  },
  sourceAlertDesc: {
    fontSize: "13px",
    color: "#475569",
    marginTop: "2px",
  },
  sourceTag: {
    display: "flex",
    alignItems: "center",
    backgroundColor: "#FFFFFF",
    padding: "6px 14px",
    borderRadius: "20px",
    fontSize: "12px",
    fontWeight: "700",
    color: "#059669",
    boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
  },
  idCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: "24px",
    overflow: "hidden",
    boxShadow:
      "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
    border: "2px solid #064E3B",
  },
  idHeader: {
    backgroundColor: "#064E3B",
    color: "#FFFFFF",
    padding: "20px 28px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottom: "4px solid #10B981",
  },
  coatOfArms: {
    width: "48px",
    height: "48px",
    borderRadius: "12px",
    backgroundColor: "rgba(255, 255, 255, 0.12)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  idHeaderTexts: {
    flex: 1,
    marginLeft: "16px",
  },
  idCountry: {
    fontSize: "18px",
    fontWeight: "900",
    letterSpacing: "1px",
  },
  idSubtitle: {
    fontSize: "10px",
    fontWeight: "600",
    color: "#A7F3D0",
    letterSpacing: "0.5px",
    marginTop: "2px",
  },
  statusBadge: {
    backgroundColor: "#10B981",
    color: "#FFFFFF",
    padding: "6px 14px",
    borderRadius: "20px",
    fontSize: "11px",
    fontWeight: "800",
    display: "flex",
    alignItems: "center",
    letterSpacing: "0.5px",
  },
  idBody: {
    padding: "32px",
    display: "flex",
    gap: "32px",
  },
  photoCol: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "180px",
  },
  photoWrapper: {
    width: "160px",
    height: "190px",
    borderRadius: "14px",
    overflow: "hidden",
    border: "2px solid #CBD5E1",
    backgroundColor: "#F1F5F9",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  photoImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  photoPlaceholder: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    width: "100%",
  },
  citizenCategory: {
    marginTop: "12px",
    padding: "4px 12px",
    backgroundColor: "#F1F5F9",
    borderRadius: "6px",
    fontSize: "11px",
    fontWeight: "800",
    color: "#475569",
    letterSpacing: "1px",
  },
  detailsCol: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  ninRow: {
    backgroundColor: "#ECFDF5",
    padding: "16px 20px",
    borderRadius: "14px",
    border: "1px solid #A7F3D0",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  ninLabel: {
    fontSize: "12px",
    fontWeight: "800",
    color: "#064E3B",
    letterSpacing: "0.5px",
  },
  ninDisplay: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  ninDigits: {
    fontSize: "22px",
    fontWeight: "900",
    color: "#064E3B",
    letterSpacing: "2px",
  },
  copyBtn: {
    background: "#FFFFFF",
    border: "1px solid #A7F3D0",
    padding: "6px 10px",
    borderRadius: "8px",
    color: "#064E3B",
    cursor: "pointer",
    fontSize: "13px",
  },
  infoGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: "16px",
  },
  infoItem: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  infoLabel: {
    fontSize: "10px",
    fontWeight: "700",
    color: "#64748B",
    letterSpacing: "0.5px",
    display: "flex",
    alignItems: "center",
  },
  infoValue: {
    fontSize: "14px",
    fontWeight: "700",
    color: "#0F172A",
  },
  addressBox: {
    backgroundColor: "#F8FAFC",
    padding: "12px 16px",
    borderRadius: "10px",
    border: "1px solid #E2E8F0",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  addressValue: {
    fontSize: "13px",
    fontWeight: "600",
    color: "#1E293B",
  },
  idFooter: {
    backgroundColor: "#F8FAFC",
    borderTop: "1px solid #E2E8F0",
    padding: "16px 28px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    fontSize: "11px",
    color: "#64748B",
  },
  footerVerificationMeta: {
    display: "flex",
    alignItems: "center",
  },
  securitySeal: {
    fontWeight: "800",
    color: "#064E3B",
    letterSpacing: "1px",
  },
  bottomActions: {
    display: "flex",
    gap: "16px",
    justifyContent: "flex-end",
  },
  printBtn: {
    display: "flex",
    alignItems: "center",
    padding: "12px 24px",
    backgroundColor: "#0F172A",
    color: "#FFFFFF",
    fontSize: "14px",
    fontWeight: "700",
    border: "none",
    borderRadius: "10px",
    cursor: "pointer",
    boxShadow: "0 4px 6px -1px rgba(15, 23, 42, 0.2)",
  },
  verifyAnotherBtn: {
    display: "flex",
    alignItems: "center",
    padding: "12px 24px",
    backgroundColor: "#FFFFFF",
    color: "#064E3B",
    fontSize: "14px",
    fontWeight: "700",
    border: "1px solid #064E3B",
    borderRadius: "10px",
    cursor: "pointer",
  },
};
